import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Plus, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { useSaveVoucherConfig, useVoucherConfig } from '../hooks/use-vouchers'
import type { VoucherTypeInput } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { usePermissions } from '@/shared/hooks/use-permissions'

type VoucherRow = {
  id: number | null
  name: string
  amounts: string
  active: boolean
  sortOrder: number
}

const EMPTY_ROW: VoucherRow = {
  id: null,
  name: '',
  amounts: '',
  active: true,
  sortOrder: 0,
}

export function VouchersPage() {
  const { t: tVouchers } = useTranslation('vouchers')
  const { t: tCommon } = useTranslation('common')
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items || []
  const [restaurantId, setRestaurantId] = useState<number | null>(null)
  const [rows, setRows] = useState<Array<VoucherRow>>([])
  const { can } = usePermissions()
  const canManage = can(ACTIONS.VOUCHER_MANAGE)

  useEffect(() => {
    if (restaurantId != null) return
    if (restaurants.length > 0) {
      setRestaurantId(restaurants[0].id)
    }
  }, [restaurantId, restaurants])

  const { data, isLoading, error } = useVoucherConfig(restaurantId)
  const saveMutation = useSaveVoucherConfig(restaurantId ?? 0)

  useEffect(() => {
    if (!data) return
    const next = data.types.map((type) => ({
      id: type.id,
      name: type.name,
      amounts: formatAmounts(type.amountsCents),
      active: type.active ?? true,
      sortOrder: type.sortOrder ?? 0,
    }))
    setRows(next.length > 0 ? next : [EMPTY_ROW])
  }, [data])

  const restaurantName = useMemo(() => {
    return (
      restaurants.find((r) => r.id === restaurantId)?.name ||
      tVouchers('restaurantPlaceholder')
    )
  }, [restaurants, restaurantId, tVouchers])

  function updateRow(index: number, patch: Partial<VoucherRow>) {
    if (!canManage) return
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    )
  }

  function addRow() {
    if (!canManage) return
    setRows((prev) => [...prev, { ...EMPTY_ROW }])
  }

  function removeRow(index: number) {
    if (!canManage) return
    setRows((prev) => prev.filter((_, i) => i !== index))
  }

  function handleSave() {
    if (!canManage) return
    if (!restaurantId) {
      toast.error(tVouchers('toasts.selectRestaurant'))
      return
    }
    const types: Array<VoucherTypeInput> = []
    for (const row of rows) {
      const name = row.name.trim()
      if (!name) {
        toast.error(tVouchers('toasts.nameRequired'))
        return
      }
      const parsed = parseAmounts(row.amounts)
      if (parsed.invalid.length > 0) {
        toast.error(
          tVouchers('toasts.invalidAmounts', {
            values: parsed.invalid.join(', '),
          }),
        )
        return
      }
      if (parsed.amountsCents.length === 0) {
        toast.error(tVouchers('toasts.addAmount', { name }))
        return
      }
      types.push({
        name,
        amountsCents: parsed.amountsCents,
        active: row.active,
        sortOrder: row.sortOrder,
      })
    }
    saveMutation.mutate(types)
  }

  return (
    <PermissionGate action={ACTIONS.VOUCHER_VIEW}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{tVouchers('title')}</h1>
            <p className="text-muted-foreground mt-1">
              {tVouchers('subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={addRow} disabled={!canManage}>
              <Plus className="mr-2 h-4 w-4" />
              {tVouchers('add')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canManage || saveMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {tCommon('save')}
            </Button>
          </div>
        </div>

        <div className="max-w-sm">
          <Label htmlFor="restaurant-select">
            {tVouchers('restaurantLabel')}
          </Label>
          <Select
            value={restaurantId ? restaurantId.toString() : ''}
            onValueChange={(value) => setRestaurantId(Number(value))}
          >
            <SelectTrigger id="restaurant-select" className="mt-1.5">
              <SelectValue placeholder={restaurantName} />
            </SelectTrigger>
            <SelectContent>
              {restaurants.map((restaurant) => (
                <SelectItem
                  key={restaurant.id}
                  value={restaurant.id.toString()}
                >
                  {restaurant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{tVouchers('error.title')}</AlertTitle>
            <AlertDescription>
              {tVouchers('error.description')}
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">
                {tVouchers('loading')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{tVouchers('table.name')}</TableHead>
                    <TableHead className="w-[260px]">
                      {tVouchers('table.amounts')}
                    </TableHead>
                    <TableHead className="w-[120px]">
                      {tVouchers('table.sort')}
                    </TableHead>
                    <TableHead className="w-[120px]">
                      {tVouchers('table.active')}
                    </TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={`${row.id ?? 'new'}-${index}`}>
                      <TableCell>
                        <Input
                          value={row.name}
                          onChange={(e) =>
                            updateRow(index, { name: e.target.value })
                          }
                          placeholder={tVouchers('placeholders.name')}
                          disabled={!canManage}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={row.amounts}
                          onChange={(e) =>
                            updateRow(index, { amounts: e.target.value })
                          }
                          placeholder={tVouchers('placeholders.amounts')}
                          disabled={!canManage}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={row.sortOrder}
                          onChange={(e) =>
                            updateRow(index, {
                              sortOrder: Number(e.target.value) || 0,
                            })
                          }
                          disabled={!canManage}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={row.active}
                            onCheckedChange={(value) =>
                              updateRow(index, { active: Boolean(value) })
                            }
                            disabled={!canManage}
                          />
                          <span className="text-sm text-muted-foreground">
                            {row.active
                              ? tCommon('active')
                              : tCommon('inactive')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeRow(index)}
                          aria-label={tVouchers('table.removeAria')}
                          disabled={!canManage}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-sm text-muted-foreground"
                      >
                        {tVouchers('table.empty')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              {tVouchers('amountsHint')}
            </p>
          </CardContent>
        </Card>
      </div>
    </PermissionGate>
  )
}

function formatAmounts(amounts: Array<number>) {
  if (amounts.length === 0) return ''
  return amounts.map((cents) => (cents / 100).toFixed(2)).join(', ')
}

function parseAmounts(input: string) {
  const parts = input
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  const amountsCents: Array<number> = []
  const invalid: Array<string> = []
  for (const part of parts) {
    const value = Number(part.replace(',', '.'))
    if (!Number.isFinite(value) || value <= 0) {
      invalid.push(part)
      continue
    }
    amountsCents.push(Math.round(value * 100))
  }
  return { amountsCents, invalid }
}
